/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as React from 'react'
import {
  Avatar,
  Button,
  Card,
  CardContent,
  createStyles,
  List,
  ListItem,
  ListItemText,
  Theme,
  Typography,
  withStyles,
  WithStyles
} from '@material-ui/core'
import { ITicket } from '../../../models/Ticket'
import { IFranchisee, IFranchisees, ISite } from '../../../models/Franchise'
import { IImageStorage } from '../../../data_sources/imagestorage/imageStorage'
import { logError } from '../../../logging'
import { PageLoader } from '../../common/PageLoader'
import TicketIcon from '@material-ui/icons/Tune'
import VisibilitySensor from 'react-visibility-sensor'
import { HSTicketDialog, INewTicketDefaults } from './HSTicketDialog'
import { OtherTicketDetails } from '../../dashboard/TicketList'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../../OneplaceLibraryProvider'
import { HSMode } from '../../../models/Hazard'

const styles = (_theme: Theme) => createStyles({})

export type LoadStatus =
  | 'loading'
  | 'loaded'
  | 'load_error'
  | 'load_error_forbidden'
  | 'load_error_user'
export type SaveStatus = 'none' | 'saving' | 'saved' | 'save_error'

export interface IHSTicketsState {
  tickets: ITicket[]
  addTicketDialogOpen: boolean
  submitDialogOpen: boolean
  submitDialogMessage?: string
  submitDialogSubMessage?: string
  loadStatus: LoadStatus
  showTicketDialog: boolean
  site: ISite
  ticketDefaults?: INewTicketDefaults
  franchisees: IFranchisees
  loadingMore: boolean
  hasMore: boolean
}

export interface IHSTicketsProp
  extends IOneplaceLibraryContextProp,
    WithStyles<typeof styles> {
  franchisee: IFranchisee
  site: ISite
  franchiseId: number
  hsMode: HSMode
  hsId: number
  hsNo: number
  updateBadge: () => void
  gotoTickets: (ticketId: number, hazardId: number) => void
  imageStorage: IImageStorage
  pageSize?: number
  autoSaveTicket: boolean
}

export default withStyles(styles)(
  withOneplaceLibraryContext(
    class HSTicketsTab extends React.Component<
      IHSTicketsProp,
      IHSTicketsState
    > {
      constructor(props: any) {
        super(props)

        this.state = {
          tickets: [],
          addTicketDialogOpen: false,
          submitDialogOpen: false,
          loadStatus: 'loading',
          showTicketDialog: false,
          site: this.props.site,
          franchisees: { franchisees: [this.props.franchisee] },
          loadingMore: false,
          hasMore: true
        }

        void this.loadTickets()
      }

      loadTickets = async () => {
        try {
          let tickets: ITicket[]
          if (this.props.hsMode === 'hazard') {
            tickets = await this.props.ctx.client.getHazardTickets(
              this.props.franchiseId,
              this.props.hsId,
              0
            )
          } else {
            tickets = await this.props.ctx.client.getIncidentTickets(
              this.props.franchiseId,
              this.props.hsId,
              0
            )
          }

          await new Promise<void>((resolve) => {
            this.setState(
              {
                tickets: tickets,
                loadStatus: 'loaded',
                hasMore: tickets.length === (this.props.pageSize || 10)
              },
              resolve
            )
          })
        } catch (e) {
          logError(e, 'Error while loading tickets data.')
          this.setState({
            loadStatus: 'load_error'
          })
        }
      }

      submitDialogOnClose = () => {
        this.setState({ submitDialogOpen: false })
      }

      onNewTicket = () => {
        this.setState({ showTicketDialog: true })
      }

      onCloseTicket = () => {
        this.setState({ showTicketDialog: false })
      }

      onCloseNewTicket = () => {
        this.setState({ showTicketDialog: false })
        void this.loadTickets()
      }

      onShowMore = async (showMore: boolean) => {
        if (showMore && this.state.hasMore && !this.state.loadingMore) {
          let fromId = 0
          if (this.state.tickets.length > 0) {
            fromId = this.state.tickets[this.state.tickets.length - 1].id!
          }
          this.setState({
            loadingMore: true
          })
          try {
            let newTickets
            if (this.props.hsMode === 'hazard') {
              newTickets = await this.props.ctx.client.getHazardTickets(
                this.props.franchiseId,
                this.props.hsId,
                fromId
              )
            } else {
              newTickets = await this.props.ctx.client.getIncidentTickets(
                this.props.franchiseId,
                this.props.hsId,
                fromId
              )
            }
            const newList = [...this.state.tickets, ...newTickets]
            this.setState({
              tickets: newList,
              loadingMore: false,
              hasMore: newTickets.length === (this.props.pageSize || 10)
            })
          } catch (e) {
            logError(e, 'Error getting more tickts')
            this.setState({
              loadingMore: false,
              hasMore: false
            })
          }
        }
      }

      render() {
        const t = this.props.ctx.i18next.t
        if (this.state.loadStatus == 'loading') {
          return (
            <div>
              <PageLoader loading={true} />
            </div>
          )
        } else if (this.state.loadStatus == 'load_error') {
          return (
            <div>
              <PageLoader loading={false} status={t('data_load_error')} />
            </div>
          )
        } else {
          return (
            <Card style={{ marginTop: 12 }}>
              <CardContent>
                <div style={{ display: 'flex' }}>
                  <Typography style={{ flexGrow: 1 }} variant='h5'>
                    {t('customLabel_tickets')}
                  </Typography>

                  <Button
                    data-testid='add-ticket-btn'
                    variant='contained'
                    color='secondary'
                    onClick={this.onNewTicket}
                  >
                    ADD {t('customLabel_ticket')}
                  </Button>
                </div>
                <List style={{ width: '100%' }} disablePadding={true}>
                  {this.state.tickets.map((ticket, idx) => {
                    return (
                      <ListItem
                        data-testid={`ticket-item-${idx}`}
                        key={idx}
                        button
                        onClick={() =>
                          this.props.gotoTickets(ticket.id!, this.props.hsId)
                        }
                      >
                        <Avatar>
                          <TicketIcon />
                        </Avatar>
                        <ListItemText
                          style={{ padding: '0 16px' }}
                          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                          primary={ticket.ticketNo + ': ' + ticket.subject}
                          secondary={
                            <OtherTicketDetails
                              ticket={ticket}
                              capabilities={this.props.ctx.client.getCapabilities()}
                            />
                          }
                        />
                      </ListItem>
                    )
                  })}
                </List>
                {this.state.showTicketDialog && (
                  <HSTicketDialog
                    isOpen={this.state.showTicketDialog}
                    franchiseId={this.props.franchiseId}
                    imageStorage={this.props.imageStorage}
                    franchisees={this.state.franchisees}
                    site={this.state.site}
                    onClose={this.onCloseTicket}
                    onCloseTicketPopDialog={this.onCloseNewTicket}
                    updateBadge={this.props.updateBadge}
                    hazardNo={
                      this.props.hsMode === 'hazard' ? this.props.hsNo : -1
                    }
                    incidentNo={
                      this.props.hsMode === 'incident' ? this.props.hsNo : -1
                    }
                    autoSave={this.props.autoSaveTicket}
                  />
                )}
              </CardContent>
              <VisibilitySensor onChange={this.onShowMore} partial={true}>
                <div style={{ minHeight: 8 }}>
                  {this.state.loadingMore && (
                    <PageLoader loading={true} paddingTop={0} />
                  )}
                </div>
              </VisibilitySensor>
            </Card>
          )
        }
      }
    }
  )
)
